/**
 * The shapes tool, from the hand going down to the shape being on the board (SRS C4).
 *
 * One gesture for all six shapes: press where it starts, drag to where it ends, let
 * go. Line and arrow run corner to corner; rectangle, circle, triangle and polygon
 * fill the box between them. The same gesture on a mouse, a stylus and a finger, which
 * is what makes the tool learnable in one go.
 *
 * The shape appears under the hand as it is dragged and lands the moment it is let
 * go. Nothing here waits for the network (docs/03-ARCHITECTURE.md §7.2): the shape is
 * on the board before it is ever shared.
 */

import type { CanvasLayerName, ViewportState } from '@opal/contracts';
import type { Layer, Point, ShapeKind, Unsubscribe } from '@opal/shared';

import { NO_EDIT, type BoardEdit } from './board-edit.js';
import type { ObjectBoard } from './object-board.js';
import { isDrawable, outlineFor } from './shape-geometry.js';
import { buildShape } from './shape-object.js';
import { drawOutline } from './shape-paint.js';
import { toViewport } from './viewport-state.js';

export interface ShapeSessionOptions {
  /** Where a finished shape lands. */
  readonly objects: ObjectBoard;
  readonly userId: string;
  /** Which shape is chosen. Read when the hand goes down and held for that drag. */
  readonly kind: () => ShapeKind;
  /** How many sides a polygon has. Read at the same moment, for the same reason. */
  readonly sides: () => number;
  readonly color: () => string;
  /** SRS C3 — the width the pen is set to, in canvas units at the current zoom. */
  readonly canvasWidth: () => number;
  /** SRS C8 — which layer a new shape goes on. Read when the hand goes down. */
  readonly layer?: () => Layer;
  readonly onChanged: (layer: CanvasLayerName) => void;
}

export interface ShapeSession {
  /** The hand went down: the shape starts here. */
  begin(at: Point): void;
  /** The hand moved: the shape now ends here. */
  extend(points: readonly Point[]): void;
  /** The hand lifted: the shape lands on the board. */
  finish(): void;
  /** The gesture was taken away — two fingers, or drawing being revoked. */
  cancel(): void;
  /** Draws the shape being dragged, on the layer the pen would use. */
  paintActive(ctx: CanvasRenderingContext2D, viewport: ViewportState): void;
  /** Called with each finished shape, for sharing and saving. */
  onEdited(handler: (edit: BoardEdit) => void): Unsubscribe;
  readonly drawing: boolean;
}

/**
 * The shape being dragged.
 *
 * Everything about how it will look is settled the moment the hand goes down, so
 * changing colour or picking another shape mid-drag cannot rewrite what is already
 * being drawn.
 */
interface Live {
  readonly kind: ShapeKind;
  readonly from: Point;
  readonly color: string;
  readonly canvasWidth: number;
  readonly sides: number;
  to: Point;
}

interface State {
  readonly options: ShapeSessionOptions;
  readonly handlers: Set<(edit: BoardEdit) => void>;
  readonly layer: () => Layer;
  live: Live | null;
}

/** The outline the drag currently describes. */
function outlineOf(live: Live): Point[] {
  return outlineFor(live.kind, live.from, live.to, live.sides);
}

/** Everything about the shape, settled the moment the hand goes down. */
function begin(options: ShapeSessionOptions, at: Point): Live {
  return {
    kind: options.kind(),
    from: at,
    to: at,
    color: options.color(),
    canvasWidth: options.canvasWidth(),
    sides: options.sides(),
  };
}

function finish(state: State): void {
  const live = state.live;
  state.live = null;
  if (!live) return;

  // A tap that never moved is not a shape, and would only leave a dot to rub out.
  if (isDrawable(live.from, live.to)) {
    const object = buildShape({
      kind: live.kind,
      points: outlineOf(live),
      userId: state.options.userId,
      color: live.color,
      canvasWidth: live.canvasWidth,
      layer: state.layer(),
      sides: live.sides,
    });
    state.options.objects.put(object);
    // Sharing and saving happen after the shape is already on the board, never before.
    for (const handler of state.handlers) handler({ ...NO_EDIT, objectsAdded: [object] });
  }

  state.options.onChanged('active');
}

export function createShapeSession(options: ShapeSessionOptions): ShapeSession {
  const state: State = {
    options,
    handlers: new Set<(edit: BoardEdit) => void>(),
    layer: options.layer ?? (() => 'content'),
    live: null,
  };

  return {
    get drawing() {
      return state.live !== null;
    },

    begin(at) {
      state.live = begin(options, at);
      options.onChanged('active');
    },

    extend(points) {
      const live = state.live;
      const last = points[points.length - 1];
      if (!live || !last) return;
      live.to = last;
      options.onChanged('active');
    },

    finish() {
      finish(state);
    },

    cancel() {
      // A shape taken away leaves nothing behind: half a rectangle is not a rectangle.
      state.live = null;
      options.onChanged('active');
    },

    paintActive(ctx, viewport) {
      const live = state.live;
      if (!live) return;
      drawOutline(
        ctx,
        outlineOf(live),
        { color: live.color, canvasWidth: live.canvasWidth },
        toViewport(viewport),
      );
    },

    onEdited(handler) {
      state.handlers.add(handler);
      return () => {
        state.handlers.delete(handler);
      };
    },
  };
}
